import {ProductInterface, SortFields} from '__generated__/__types__';
import {useTranslation} from 'next-i18next';
import {useEffect, useRef} from 'react';
import ProductSort from '@/_application/modules/categories/components/product-sort';
import {useProductsFilterActions} from '@/_application/modules/products/hooks/use-product-filter-actions';
import rangeMap from '@/lib/range-map';
import ProductLoader from '@/_application/core/components/ui/loaders/product-loader';
import {ProductCard} from '@/_application/modules/products/components/cards/product-card';
import {BrandProductsFilter} from '@/_application/pages/brand/components/brand-products-filter';
import {PRODUCTS_PER_PAGE} from '@/framework/client/variables';
import CategoryProductsPagination from '@/_application/modules/categories/components/category-products-pagination';
import {HighlightBrand} from '@/__generated__/__types__';
import {LazyLoadBlock} from '@/_application/core/components/commons/lazy-load-block';
import {ProductListItemsQueryOptions} from '@/_application/modules/products/request/product-list.request';
import {ProductListItemsQuery} from '@/framework/gql/products.graphql';

interface IProps {
    brand: HighlightBrand;
    initialProducts?: ProductListItemsQuery['products'];
    productListItemsQueryOptions?: ProductListItemsQueryOptions;
}

function BrandProducts({
    brand,
    initialProducts,
    productListItemsQueryOptions = {
        filter: {},
        sort: {},
        search: '',
        currentPage: 1,
        pageSize: PRODUCTS_PER_PAGE,
    } as ProductListItemsQueryOptions,
}: Readonly<IProps>) {
    const {t} = useTranslation('common');
    const {
        fetchData,
        variables,
        products,
        error,
        isLoading,
        handleFilterChange,
        handlePaginationChange,
        handleSortChange,
    } = useProductsFilterActions();

    // Track if this is the first render
    const isFirstRender = useRef(true);

    // Track previous variables to detect actual user changes
    const prevVariablesRef = useRef<string | null>(null);

    // Use fetched products if available, otherwise use initialProducts from server
    const productsResponse = products?.items?.length ? products : initialProducts;

    useEffect(() => {
        const currentVariables = JSON.stringify(variables);

        // On first render, just store the variables and don't fetch (we have initialProducts)
        if (isFirstRender.current) {
            isFirstRender.current = false;
            prevVariablesRef.current = currentVariables;
            return;
        }

        // Only fetch if variables actually changed from previous render
        if (prevVariablesRef.current !== currentVariables) {
            prevVariablesRef.current = currentVariables;
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(variables)]);

    if (error) {
        return (
            <div className="flex h-80 w-[1204px] items-center justify-center rounded-2xl bg-white">
                <p className="text-xl font-bold text-KPZ_red">{t('Internal server error')}</p>
            </div>
        );
    }

    return (
        <section className={'product-grid-wrapper mt-5'}>
            <div className={'flex gap-5'}>
                <BrandProductsFilter brand={brand} onFilterChange={handleFilterChange} />
                <div className="w-[976px] rounded-2xl bg-white p-5">
                    <div className="flex items-center justify-between">
                        <h1 className="text-xl font-bold text-primary">
                            {t('text-product')}
                            {` (${brand?.label})`}
                        </h1>
                        <ProductSort
                            sortFields={productsResponse?.sort_fields as SortFields}
                            onSortChange={handleSortChange}
                        />
                    </div>

                    {/* Product */}
                    <div className="flex">
                        <div className="mt-5 grid gap-5 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4">
                            {isLoading && !productsResponse?.items?.length
                                ? rangeMap(20, i => <ProductLoader key={i} uniqueKey={`product-${i}`} />)
                                : productsResponse?.items?.map((product, index) => {
                                      const productItem = product as ProductInterface;
                                      if (!productItem) return <></>;
                                      return (
                                          <LazyLoadBlock key={productItem?.id} isLazyLoad={index >= 10}>
                                              <ProductCard product={productItem} key={`${productItem?.id}`} />
                                          </LazyLoadBlock>
                                      );
                                  })}
                        </div>
                    </div>
                    {!isLoading && !productsResponse?.items?.length && (
                        <div className="flex h-96 w-full items-center justify-center rounded-2xl bg-white">
                            <p className="text-xl font-bold text-primary">{t('brand-has-no-product')}</p>
                        </div>
                    )}
                    {/* Pagination */}
                    {productsResponse?.page_info?.total_pages! > 1 && (
                        <div className="mt-5">
                            <CategoryProductsPagination
                                pageSize={productsResponse?.page_info?.page_size ?? PRODUCTS_PER_PAGE}
                                totalItems={productsResponse?.total_count as number}
                                handlePaginationChange={handlePaginationChange}
                            />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

export default BrandProducts;
